import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AuthExpiredError,
  deleteIm,
  imDownloadHref,
  imPreviewHref,
  listIms,
  patchIm,
  uploadIm,
  type Im,
  type PatchImFields,
  type UploadOutcome,
} from '../api/ims.js';
import { OwnerBadge, useSession } from '../components/Session.js';
import { datetime, money, plain } from '../format.js';

// Whether the signed-in person may change the row being rendered (its owner,
// or an admin). Others see the values as plain text.
const RowEditableContext = createContext(true);

type LoadState = 'loading' | 'ready' | 'error';
type Notice = { tone: 'error' | 'info' | 'success'; text: string };

// EBITDA margin (EBITDA as a % of revenue) is derived, never stored — edit
// revenue/EBITDA and it follows. Null when either input is missing/invalid or
// revenue is zero.
function ebitdaMarginPct(im: Im): number | null {
  if (im.revenue === null || im.revenue === '' || im.ebitda === null || im.ebitda === '') return null;
  const revenue = Number(im.revenue);
  const ebitda = Number(im.ebitda);
  if (!Number.isFinite(revenue) || !Number.isFinite(ebitda) || revenue === 0) return null;
  return (ebitda / revenue) * 100;
}

function pct(v: number | null): string {
  return v === null ? '—' : `${Math.round(v)}%`;
}

// A boolean cell rendered as a single toggle button. Clicking flips the value
// and PATCHes immediately; while in flight it's disabled + dimmed.
function ToggleCell({
  value,
  onToggle,
  saving,
  offTone = 'neutral',
}: {
  value: boolean;
  onToggle: (next: boolean) => void;
  saving: boolean;
  offTone?: 'neutral' | 'negative';
}) {
  const editable = useContext(RowEditableContext);
  const onCls = 'bg-positive-500/15 text-positive-500';
  const offCls = offTone === 'negative' ? 'bg-negative-500/15 text-negative-500' : 'bg-white/5 text-ink-300';
  return (
    <button
      type="button"
      disabled={saving || !editable}
      aria-pressed={value}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(!value);
      }}
      className={`pill ${value ? onCls : offCls} disabled:opacity-50`}
    >
      {value ? 'Yes' : 'No'}
    </button>
  );
}

// An inline-editable text/number cell. Click (or focus + Enter) to edit; Enter
// or blur commits, Escape cancels. Numbers are parsed before saving; empty
// clears the field to null. A no-op edit doesn't PATCH.
function EditableCell({
  raw,
  display,
  kind,
  onCommit,
  saving,
  inputClass = 'w-28',
  align = 'left',
}: {
  raw: string | null;
  display: React.ReactNode;
  kind: 'text' | 'number';
  onCommit: (next: string | number | null) => void;
  saving: boolean;
  inputClass?: string;
  align?: 'left' | 'right';
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const doneRef = useRef(false);
  const editable = useContext(RowEditableContext);

  const begin = () => {
    doneRef.current = false;
    setDraft(raw ?? '');
    setEditing(true);
  };

  const cancel = () => {
    doneRef.current = true; // suppress the trailing blur-commit
    setEditing(false);
  };

  const commit = () => {
    if (doneRef.current) return; // Enter already committed; ignore the blur
    doneRef.current = true;
    setEditing(false);
    const trimmed = draft.trim();
    if (kind === 'number') {
      const next = trimmed === '' ? null : Number(trimmed);
      if (next !== null && Number.isNaN(next)) return; // invalid → discard
      const cur = raw === null || raw === '' ? null : Number(raw);
      if (next === cur) return; // no-op
      onCommit(next);
    } else {
      const next = trimmed === '' ? null : trimmed;
      if ((next ?? '') === (raw ?? '')) return; // no-op
      onCommit(next);
    }
  };

  if (!editable) {
    return <span className={`text-sm ${align === 'right' ? 'block text-right' : ''}`}>{display}</span>;
  }

  if (editing) {
    return (
      <input
        autoFocus
        type={kind === 'number' ? 'number' : 'text'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        className={`bg-ink-900 border border-positive-500/50 rounded px-1.5 py-0.5 text-sm outline-none ${inputClass} ${
          align === 'right' ? 'text-right' : ''
        }`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        begin();
      }}
      // Show the full value on hover; the cell itself stays one line. Without a
      // width cap + truncate, a long field (e.g. a verbose tech_stack) wraps and
      // stretches the whole row vertically. Click to edit reveals it in full.
      title={typeof raw === 'string' && raw ? raw : 'Click to edit'}
      className={`block truncate rounded px-1.5 py-0.5 text-sm hover:bg-white/5 ${inputClass} ${
        align === 'right' ? 'text-right tabular-nums' : 'text-left'
      } ${saving ? 'opacity-50' : ''}`}
    >
      {display}
    </button>
  );
}

/** Our rating out of 10, coloured like the board's. */
function RatingChip({ value }: { value: string | number }) {
  const n = Number(value);
  const tone =
    n >= 7
      ? 'bg-positive-500/15 text-positive-400 ring-positive-500/25'
      : n >= 5
        ? 'bg-warn-500/10 text-warn-400 ring-warn-500/20'
        : 'bg-white/[0.04] text-ink-400 ring-white/10';
  return (
    <span
      className={`inline-flex min-w-[2.5rem] justify-center rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums ring-1 ring-inset ${tone}`}
    >
      {n.toFixed(1)}
    </span>
  );
}

// A human label for an IM, preferring the rated name, then the uploaded
// filename, then the deal number.
function imLabel(im: Im): string {
  return im.name ?? im.original_filename ?? `IM #${im.opportunity_number}`;
}

function ImRow({
  im,
  onPatch,
  onPreview,
  onDelete,
  savingCells,
}: {
  im: Im;
  onPatch: (id: string, field: string, fields: PatchImFields) => void;
  onPreview: (im: Im) => void;
  onDelete: (im: Im) => void;
  savingCells: Set<string>;
}) {
  const navigate = useNavigate();
  const { canEdit, isAdmin, ownerName } = useSession();
  const commit = (field: keyof PatchImFields, value: string | number | boolean | null) =>
    onPatch(im.id, field, { [field]: value } as PatchImFields);
  const saving = (field: string) => savingCells.has(`${im.id}:${field}`);

  // The AI rating populates asynchronously (on upload + the daily batch). Until
  // it lands, show "Rating…" rather than a blank "—" that reads as stuck; if the
  // rating errored, show "Failed" with the message on hover. A human-entered
  // rating (initial_rating set) always wins.
  const ratingDisplay =
    im.initial_rating !== null ? (
      <RatingChip value={im.initial_rating} />
    ) : im.rate_error !== null ? (
      <span className="text-negative-500 text-xs" title={im.rate_error}>
        Failed
      </span>
    ) : (
      <span className="text-ink-500 text-xs animate-pulse">Rating…</span>
    );

  return (
    <RowEditableContext.Provider value={canEdit(im)}>
    <tr
      onClick={() => navigate(`/ims/${im.id}`)}
      className={`cursor-pointer border-b border-white/[0.04] align-middle transition-colors last:border-0 hover:bg-white/[0.035] ${
        im.still_in_running ? '' : 'text-ink-400 opacity-60 hover:opacity-100'
      }`}
    >
      <td className="px-3 py-2 tabular-nums text-ink-300">
        <span className="inline-flex items-center gap-1.5">
          <OwnerBadge email={im.owner_email} name={ownerName(im.owner_email)} />
          {im.opportunity_number}
        </span>
      </td>
      <td className="px-3 py-2">
        <ToggleCell
          value={im.still_in_running}
          onToggle={(next) => commit('still_in_running', next)}
          saving={saving('still_in_running')}
          offTone="negative"
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="text"
          raw={im.broker_reference}
          display={plain(im.broker_reference)}
          saving={saving('broker_reference')}
          inputClass="w-28"
          onCommit={(v) => commit('broker_reference', v)}
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="text"
          raw={im.name}
          display={
            im.name ?? <span className="text-ink-500">{im.original_filename ?? '—'}</span>
          }
          saving={saving('name')}
          inputClass="w-56 font-medium text-ink-100"
          onCommit={(v) => commit('name', v)}
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="text"
          raw={im.founder}
          display={plain(im.founder)}
          saving={saving('founder')}
          inputClass="w-32"
          onCommit={(v) => commit('founder', v)}
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="number"
          raw={im.initial_rating}
          display={ratingDisplay}
          saving={saving('initial_rating')}
          inputClass="w-16"
          align="right"
          onCommit={(v) => commit('initial_rating', v)}
        />
      </td>
      <td className="px-3 py-2">
        <ToggleCell
          value={im.first_call_requested}
          onToggle={(next) => commit('first_call_requested', next)}
          saving={saving('first_call_requested')}
        />
      </td>
      <td className="px-3 py-2">
        <ToggleCell
          value={im.first_call_scheduled}
          onToggle={(next) => commit('first_call_scheduled', next)}
          saving={saving('first_call_scheduled')}
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="number"
          raw={im.revenue}
          display={money(im.revenue)}
          saving={saving('revenue')}
          inputClass="w-24"
          align="right"
          onCommit={(v) => commit('revenue', v)}
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="number"
          raw={im.ebitda}
          display={money(im.ebitda)}
          saving={saving('ebitda')}
          inputClass="w-24"
          align="right"
          onCommit={(v) => commit('ebitda', v)}
        />
      </td>
      {/* Derived from revenue + EBITDA, so read-only — matches EditableCell's
          inner padding so the column lines up with its editable neighbours. */}
      <td className="px-3 py-2">
        <span className="block w-16 px-1.5 py-0.5 text-right text-sm tabular-nums text-ink-300">
          {pct(ebitdaMarginPct(im))}
        </span>
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="number"
          raw={im.asking_for}
          display={money(im.asking_for)}
          saving={saving('asking_for')}
          inputClass="w-24"
          align="right"
          onCommit={(v) => commit('asking_for', v)}
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="number"
          raw={im.offered}
          display={money(im.offered)}
          saving={saving('offered')}
          inputClass="w-24"
          align="right"
          onCommit={(v) => commit('offered', v)}
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="number"
          raw={im.num_clients === null ? null : String(im.num_clients)}
          display={plain(im.num_clients)}
          saving={saving('num_clients')}
          inputClass="w-16"
          align="right"
          onCommit={(v) => commit('num_clients', v)}
        />
      </td>
      <td className="px-3 py-2">
        <EditableCell
          kind="text"
          raw={im.outcome}
          display={plain(im.outcome)}
          saving={saving('outcome')}
          inputClass="w-32"
          onCommit={(v) => commit('outcome', v)}
        />
      </td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-ink-300">
        {datetime(im.created_at)}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPreview(im);
            }}
            className="text-xs font-medium px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-ink-100"
          >
            Preview
          </button>
          <a
            href={imPreviewHref(im.id)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs font-medium px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-ink-100"
          >
            Open
          </a>
          <a
            href={imDownloadHref(im.id)}
            download
            onClick={(e) => e.stopPropagation()}
            className="text-xs font-medium px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-ink-100"
          >
            Download
          </a>
          {isAdmin ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(im);
            }}
            className="text-xs font-medium px-2 py-1 rounded-md bg-negative-500/10 hover:bg-negative-500/20 text-negative-500"
          >
            Delete
          </button>
          ) : null}
        </div>
      </td>
    </tr>
    </RowEditableContext.Provider>
  );
}

// One labelled value in a card's financial grid. Truncates so a long broker ref
// can't blow out the row.
function CardField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-ink-300">{label}</dt>
      <dd className="truncate tabular-nums text-ink-100">{value}</dd>
    </div>
  );
}

// A single deal as a stacked card, for narrow (phone) screens where the
// 16-column table can't fit. Tap anywhere to open the detail view (full
// editing); the status toggles and action buttons act in place and stop the
// tap from navigating.
function ImCard({
  im,
  onPatch,
  onPreview,
  onDelete,
  savingCells,
}: {
  im: Im;
  onPatch: (id: string, field: string, fields: PatchImFields) => void;
  onPreview: (im: Im) => void;
  onDelete: (im: Im) => void;
  savingCells: Set<string>;
}) {
  const navigate = useNavigate();
  const { canEdit, isAdmin } = useSession();
  const commit = (field: keyof PatchImFields, value: string | number | boolean | null) =>
    onPatch(im.id, field, { [field]: value } as PatchImFields);
  const saving = (field: string) => savingCells.has(`${im.id}:${field}`);

  const rating =
    im.initial_rating !== null ? (
      <span className="pill bg-white/5 text-ink-100">Rated {im.initial_rating}/10</span>
    ) : im.rate_error !== null ? (
      <span className="pill bg-negative-500/15 text-negative-500" title={im.rate_error}>
        Rating failed
      </span>
    ) : (
      <span className="pill animate-pulse bg-white/5 text-ink-500">Rating…</span>
    );

  return (
    <RowEditableContext.Provider value={canEdit(im)}>
    <div
      onClick={() => navigate(`/ims/${im.id}`)}
      className={`card cursor-pointer space-y-3 p-4 ${
        im.still_in_running ? '' : 'bg-negative-500/10'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-ink-100">{imLabel(im)}</div>
          <div className="mt-0.5 text-xs text-ink-300">
            #{im.opportunity_number}
            {im.founder ? ` · ${im.founder}` : ''}
          </div>
        </div>
        <div className="shrink-0">{rating}</div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-300">
        <span className="flex items-center gap-1.5">
          Still running
          <ToggleCell
            value={im.still_in_running}
            onToggle={(next) => commit('still_in_running', next)}
            saving={saving('still_in_running')}
            offTone="negative"
          />
        </span>
        <span className="flex items-center gap-1.5">
          First call requested
          <ToggleCell
            value={im.first_call_requested}
            onToggle={(next) => commit('first_call_requested', next)}
            saving={saving('first_call_requested')}
          />
        </span>
        <span className="flex items-center gap-1.5">
          First call scheduled
          <ToggleCell
            value={im.first_call_scheduled}
            onToggle={(next) => commit('first_call_scheduled', next)}
            saving={saving('first_call_scheduled')}
          />
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <CardField label="Revenue" value={money(im.revenue)} />
        <CardField label="EBITDA" value={money(im.ebitda)} />
        <CardField label="EBITDA margin" value={pct(ebitdaMarginPct(im))} />
        <CardField label="Asking for" value={money(im.asking_for)} />
        <CardField label="Offered" value={money(im.offered)} />
        <CardField label="Clients" value={plain(im.num_clients)} />
        <CardField label="Broker ref" value={plain(im.broker_reference)} />
      </dl>

      {im.outcome ? (
        <div className="text-sm">
          <span className="text-ink-300">Outcome: </span>
          <span className="text-ink-100">{im.outcome}</span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 border-t border-white/5 pt-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPreview(im);
          }}
          className="rounded-md bg-white/5 px-3 py-1.5 text-xs font-medium text-ink-100 hover:bg-white/10"
        >
          Preview
        </button>
        <a
          href={imPreviewHref(im.id)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="rounded-md bg-white/5 px-3 py-1.5 text-xs font-medium text-ink-100 hover:bg-white/10"
        >
          Open
        </a>
        <a
          href={imDownloadHref(im.id)}
          download
          onClick={(e) => e.stopPropagation()}
          className="rounded-md bg-white/5 px-3 py-1.5 text-xs font-medium text-ink-100 hover:bg-white/10"
        >
          Download
        </a>
        {isAdmin ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(im);
          }}
          className="ml-auto rounded-md bg-negative-500/10 px-3 py-1.5 text-xs font-medium text-negative-500 hover:bg-negative-500/20"
        >
          Delete
        </button>
        ) : null}
      </div>
    </div>
    </RowEditableContext.Provider>
  );
}

type SortType = 'number' | 'string' | 'boolean';
// Stored fields sort by their Im key; 'ebitda_margin' is computed on the fly.
type SortKey = keyof Im | 'ebitda_margin';
interface ColumnDef {
  label: string;
  key: SortKey | null; // null = not sortable (Actions)
  type?: SortType;
}

const COLUMNS: ColumnDef[] = [
  { label: '#', key: 'opportunity_number', type: 'number' },
  { label: 'Still in running?', key: 'still_in_running', type: 'boolean' },
  { label: 'Broker ref', key: 'broker_reference', type: 'string' },
  { label: 'Name', key: 'name', type: 'string' },
  { label: 'Founder', key: 'founder', type: 'string' },
  { label: 'Rating', key: 'initial_rating', type: 'number' },
  { label: 'First call requested?', key: 'first_call_requested', type: 'boolean' },
  { label: 'First call scheduled?', key: 'first_call_scheduled', type: 'boolean' },
  { label: 'Revenue', key: 'revenue', type: 'number' },
  { label: 'EBITDA', key: 'ebitda', type: 'number' },
  { label: 'EBITDA %', key: 'ebitda_margin', type: 'number' },
  { label: 'Asking for', key: 'asking_for', type: 'number' },
  { label: 'Offered', key: 'offered', type: 'number' },
  { label: 'Clients', key: 'num_clients', type: 'number' },
  { label: 'Outcome', key: 'outcome', type: 'string' },
  { label: 'Uploaded at', key: 'created_at', type: 'string' },
  { label: 'Actions', key: null },
];

interface SortState {
  key: SortKey;
  type: SortType;
  dir: 'asc' | 'desc';
}

// Sort rows by a column. Empty/null values always sink to the bottom, whichever
// direction is active. Name falls back to the uploaded filename.
function sortIms(rows: Im[], sort: SortState | null): Im[] {
  if (!sort) return rows;
  const { key, type, dir } = sort;
  const factor = dir === 'asc' ? 1 : -1;
  const valueOf = (im: Im): unknown => {
    if (key === 'ebitda_margin') return ebitdaMarginPct(im);
    if (key === 'name') return im.name ?? im.original_filename ?? '';
    return im[key];
  };
  return [...rows].sort((a, b) => {
    const av = valueOf(a);
    const bv = valueOf(b);
    const aEmpty = av === null || av === undefined || av === '';
    const bEmpty = bv === null || bv === undefined || bv === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    let cmp: number;
    if (type === 'number') cmp = Number(av) - Number(bv);
    else if (type === 'boolean') cmp = (av === true ? 1 : 0) - (bv === true ? 1 : 0);
    else cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
    return cmp * factor;
  });
}

function ImTable({
  ims,
  onPatch,
  onPreview,
  onDelete,
  savingCells,
  sort,
  onSort,
}: {
  ims: Im[];
  onPatch: (id: string, field: string, fields: PatchImFields) => void;
  onPreview: (im: Im) => void;
  onDelete: (im: Im) => void;
  savingCells: Set<string>;
  sort: SortState | null;
  onSort: (col: ColumnDef) => void;
}) {
  if (ims.length === 0) {
    return (
      <div className="card p-8 text-center text-ink-300 text-sm">
        No deals yet. Drop an IM PDF above to get started.
      </div>
    );
  }
  const rows = sortIms(ims, sort);
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left text-[11px] uppercase tracking-wider text-ink-400">
            {COLUMNS.map((c) => {
              const active = sort !== null && sort.key === c.key;
              return (
                <th key={c.label} className="whitespace-nowrap px-3 py-2.5 font-medium">
                  {c.key === null ? (
                    c.label
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSort(c)}
                      className="inline-flex items-center gap-1 hover:text-ink-100"
                    >
                      {c.label}
                      <span className="text-accent-400">
                        {active ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                      </span>
                    </button>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((im) => (
            <ImRow
              key={im.id}
              im={im}
              onPatch={onPatch}
              onPreview={onPreview}
              onDelete={onDelete}
              savingCells={savingCells}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// In-app PDF preview. The <iframe> points at the auth-gated /preview endpoint
// (cookie sent automatically by the browser); the server 302s to a short-lived
// signed URL (GCS) or streams the bytes inline (local) — never a durable public
// link. Closes on Escape or a backdrop click.
function PreviewModal({ im, onClose }: { im: Im; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = imLabel(im);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${title}`}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden p-0"
      >
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <div className="truncate text-sm font-medium">{title}</div>
          <div className="flex shrink-0 items-center gap-2">
            <a
              href={imDownloadHref(im.id)}
              download
              className="rounded-md bg-white/5 px-2.5 py-1 text-xs font-medium text-ink-100 hover:bg-white/10"
            >
              Download
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="rounded-md bg-white/5 px-2.5 py-1 text-xs font-medium text-ink-100 hover:bg-white/10"
            >
              Close
            </button>
          </div>
        </div>
        <iframe
          title={`Preview of ${title}`}
          src={imPreviewHref(im.id)}
          className="w-full flex-1 bg-white"
        />
      </div>
    </div>
  );
}

export function ImListView() {
  const [ims, setIms] = useState<Im[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set());
  const [previewIm, setPreviewIm] = useState<Im | null>(null);
  // Default: newest upload first (ISO timestamps sort correctly as strings).
  // Click any header to re-sort.
  const [sort, setSort] = useState<SortState | null>({
    key: 'created_at',
    type: 'string',
    dir: 'desc',
  });
  const inputRef = useRef<HTMLInputElement>(null);

  // Click a header to sort by it; click the active header again to flip
  // direction. Actions (key === null) isn't sortable.
  const onSort = useCallback((col: ColumnDef) => {
    if (col.key === null) return;
    const key = col.key;
    setSort((prev) =>
      prev && prev.key === key
        ? { ...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, type: col.type ?? 'string', dir: 'asc' },
    );
  }, []);

  // Delete a deal after a confirm; on success drop it from the list. The PDF is
  // removed server-side too.
  const onDelete = useCallback(async (im: Im) => {
    const label = im.name ?? im.original_filename ?? `IM #${im.opportunity_number}`;
    if (!window.confirm(`Delete "${label}"? This removes the deal and its PDF and can't be undone.`)) {
      return;
    }
    try {
      await deleteIm(im.id);
      setIms((prev) => prev.filter((row) => row.id !== im.id));
      setNotice({ tone: 'success', text: `Deleted "${label}".` });
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        window.location.reload();
        return;
      }
      setNotice({ tone: 'error', text: e instanceof Error ? e.message : "Couldn't delete." });
    }
  }, []);

  // Inline-edit a single cell: PATCH the field, then replace the row in state
  // with the server's authoritative response (so derived fields like
  // rejected_at reflect the toggle). A 401/403 bounces through the auth gate.
  const patchCell = useCallback(async (id: string, field: string, fields: PatchImFields) => {
    const key = `${id}:${field}`;
    setSavingCells((s) => new Set(s).add(key));
    try {
      const updated = await patchIm(id, fields);
      setIms((prev) => prev.map((row) => (row.id === id ? updated : row)));
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        window.location.reload();
        return;
      }
      setNotice({ tone: 'error', text: e instanceof Error ? e.message : "Couldn't save change." });
    } finally {
      setSavingCells((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const rows = await listIms();
      setIms(rows);
      setLoadState('ready');
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        window.location.reload();
        return;
      }
      setLoadState('error');
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Auto-refresh while any IM is still being rated. Rating happens server-side
  // and asynchronously (kicked off on upload, plus the daily batch), so a row
  // can land here unrated and fill in seconds later. We re-fetch every few
  // seconds until nothing is pending — a row counts as pending when it has no
  // score (initial_rating) and no recorded error yet. The poll self-terminates
  // when all rows are resolved, and is bounded so a misconfigured provider
  // (which never records an error on the row) can't poll indefinitely.
  const pollCount = useRef(0);
  useEffect(() => {
    const pending = ims.some(
      (im) => im.initial_rating === null && im.rate_error === null,
    );
    if (!pending) {
      pollCount.current = 0; // all resolved — reset for the next upload
      return;
    }
    if (pollCount.current >= 20) return; // ~1 min of polling, then give up
    const t = setTimeout(() => {
      pollCount.current += 1;
      void refresh();
    }, 3000);
    return () => clearTimeout(t);
  }, [ims, refresh]);

  const handleFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      if (files.length === 0 || uploading) return;

      setNotice(null);
      setUploading(true);

      let created = 0;
      let duplicates = 0;
      let firstError: string | null = null;
      const outcomes: UploadOutcome[] = [];

      for (const file of files) {
        setProgress(0);
        const outcome = await uploadIm(file, setProgress);
        outcomes.push(outcome);
        if (outcome.kind === 'auth') break;
      }

      setProgress(null);
      setUploading(false);

      // Session lapsed mid-upload — bounce through the auth gate.
      if (outcomes.some((o) => o.kind === 'auth')) {
        window.location.reload();
        return;
      }

      for (const o of outcomes) {
        if (o.kind === 'created') created += 1;
        else if (o.kind === 'duplicate') duplicates += 1;
        else if ((o.kind === 'rejected' || o.kind === 'error') && !firstError) firstError = o.message;
      }

      await refresh();

      // Surface the most actionable single notice: errors first, then dupes.
      if (firstError) {
        setNotice({ tone: 'error', text: firstError });
      } else if (duplicates > 0 && created === 0) {
        setNotice({ tone: 'info', text: 'That document is already in your list.' });
      } else if (created > 0) {
        const dupeNote = duplicates > 0 ? ` (${duplicates} already on the list)` : '';
        setNotice({
          tone: 'success',
          text: `Added ${created} deal${created === 1 ? '' : 's'}${dupeNote}.`,
        });
      }
    },
    [refresh, uploading],
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    void handleFiles(e.dataTransfer.files);
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <section>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Deals</h1>
          <p className="mt-1 text-sm text-ink-400">
            Every IM and teaser, whether it came in by email or was uploaded here.
          </p>
        </div>
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label="Upload an IM PDF"
        aria-disabled={uploading}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        className={`mb-4 cursor-pointer rounded-xl border border-dashed px-4 py-3 text-center transition-colors ${
          dragActive
            ? 'border-accent-400 bg-accent-500/10'
            : 'border-white/10 bg-white/[0.015] hover:border-white/20 hover:bg-white/[0.03]'
        } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {uploading ? (
          <div className="space-y-2">
            <div className="text-sm text-ink-100">Uploading…</div>
            <div className="h-1.5 w-full max-w-xs mx-auto rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-accent-400 transition-all"
                style={{ width: `${Math.round((progress ?? 0) * 100)}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="text-sm text-ink-300">
            <span className="mr-1.5 text-ink-400">↑</span>
            Drop an IM PDF here, or <span className="font-medium text-accent-300">choose a file</span>
            <span className="ml-2 text-xs text-ink-500">PDF only · up to 25 MB</span>
          </div>
        )}
      </div>

      {notice ? (
        <div
          className={`text-xs rounded-md p-2 mb-4 border ${
            notice.tone === 'error'
              ? 'text-negative-500 border-negative-500/30'
              : notice.tone === 'success'
                ? 'text-positive-500 border-positive-500/30'
                : 'text-ink-300 border-white/10'
          }`}
        >
          {notice.text}
        </div>
      ) : null}

      {loadState === 'loading' ? (
        <div className="card p-8 text-center text-ink-300 text-sm">Loading deals…</div>
      ) : loadState === 'error' ? (
        <div className="card p-8 text-center space-y-3">
          <div className="text-sm text-negative-500">Couldn't load deals.</div>
          {loadError ? <div className="text-xs text-ink-300 break-words">{loadError}</div> : null}
          <button
            onClick={() => {
              setLoadState('loading');
              void refresh();
            }}
            className="text-sm font-medium px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-ink-100"
          >
            Retry
          </button>
        </div>
      ) : ims.length === 0 ? (
        <div className="card p-8 text-center text-ink-300 text-sm">
          No deals yet. Drop an IM PDF above to get started.
        </div>
      ) : (
        <>
          {/* Phone: stacked cards (the 16-column table can't fit). Sorted
              newest first; tap a card to edit in the detail view. */}
          <div className="space-y-3 md:hidden">
            {sortIms(ims, sort).map((im) => (
              <ImCard
                key={im.id}
                im={im}
                onPatch={patchCell}
                onPreview={setPreviewIm}
                onDelete={onDelete}
                savingCells={savingCells}
              />
            ))}
          </div>
          {/* Tablet / desktop: full editable, sortable table. */}
          <div className="hidden md:block">
            <ImTable
              ims={ims}
              onPatch={patchCell}
              onPreview={setPreviewIm}
              onDelete={onDelete}
              savingCells={savingCells}
              sort={sort}
              onSort={onSort}
            />
          </div>
        </>
      )}

      {previewIm ? <PreviewModal im={previewIm} onClose={() => setPreviewIm(null)} /> : null}
    </section>
  );
}
